import MatchingChip from 'components/Chips/MatchingChip';
import { ActionDrawer } from 'components/Drawer';
import CheckboxList from 'components/Drawer/CheckboxList';
import EllipsisText from 'components/EllipsisText';
import LabelledCheckbox from 'components/form/Inputs/LabelledCheckbox';
import SettingsIcon from 'components/icons/Settings';
import List from 'components/list';
import {
  ChooseMentor,
  ChooseMentorContainer,
  CurrentMentor,
} from 'components/list/ChooseMentor';
import Priority from 'components/list/Priority';
import SettingsContainer from 'components/list/SettingsContainer';
import { BoldTd, Td } from 'components/list/Td';
import { getOrderTh, Th } from 'components/list/Th';
import { getColumnNumber } from 'components/list/utils';
import CONSIDERATIONS from 'enums/Considerations';
import EXPERTISE from 'enums/Expertise';
import FORMATIONS from 'enums/Formation';
import HOBBIES from 'enums/Hobby';
import { MENTOR_PRIORITY_LEVELS } from 'enums/PriorityLevels';
import STUDY_LEVELS from 'enums/StudyLevels';
import React, { FC, useContext, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { setActiveColumns } from 'store/actions/admin/menteeLists';
import { StoreState } from 'store/reducers';
import { Action } from 'types/typeguards/reducerActions';
import { getLocaleDate } from 'utils/time';
import { MenteeAssociateContext, sortableColumns } from '.';
import Button from 'components/Button';

export const columnNames = [
  'matching',
  'softMatching',
  'hardMatching',
  'firstName',
  'lastName',
  'priority',
  'organism',
  'createdAt',
  'region',
  'postalCode',
  'wantedMentorship',
  'assignedMentorship',
  'jobSearch',
  'careerPlan',
  'studies',
  'entrepreneurship',
  'expertiseFields',
  'formations',
  'diploma',
  'consideration',
  'hobby',
] as const;
export type ColumnNames = typeof columnNames[number];
const OrderTh = getOrderTh<typeof sortableColumns[number]>();

export type Columns = {
  [key in ColumnNames]: boolean;
};

export const initialActiveColumns: Columns = {
  matching: true,
  softMatching: false,
  hardMatching: false,
  firstName: true,
  lastName: true,
  priority: true,
  organism: true,
  createdAt: true,
  region: false,
  postalCode: false,
  wantedMentorship: true,
  assignedMentorship: true,
  // Mentor form questions
  jobSearch: false,
  careerPlan: false,
  studies: false,
  entrepreneurship: false,
  expertiseFields: false,
  formations: false,
  diploma: false,
  consideration: false,
  hobby: false,
};
type Props = {
  activeColumns: Columns;
  setActiveColumns: (activeColumns: Columns) => void;
};
const MentoringList: FC<Props> = ({ activeColumns, setActiveColumns }) => {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { mentee, mentors, pages, page, setPage, loading, order, setOrder } =
    useContext(MenteeAssociateContext);

  const [selectedColumns, setSelectedColumns] = useState(activeColumns);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setSelectedColumns({
      ...selectedColumns,
      [e.target.name]: e.target.checked,
    });
  };

  const columnNumber = useMemo(() => {
    return getColumnNumber(activeColumns, false);
  }, [activeColumns]);

  return (
    <>
      <ActionDrawer
        open={settingsOpen}
        close={() => setSettingsOpen(false)}
        title="Colonnes mentors"
        subtitle="Activer les colonnes que vous souhaitez"
        cancel={() => {
          setSelectedColumns(activeColumns);
        }}
        apply={() => {
          setActiveColumns(selectedColumns);
        }}
      >
        <CheckboxList>
          <LabelledCheckbox
            label="Matching"
            checked={selectedColumns.matching}
            name="matching"
            onChange={handleChange}
          />
          {/* 
            DISABLED 
          
          <LabelledCheckbox
            label="Matching soft"
            checked={selectedColumns.softMatching}
            name="softMatching"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Matching hard"
            checked={selectedColumns.hardMatching}
            name="hardMatching"
            onChange={handleChange}
          /> */}
          <LabelledCheckbox
            label="Prioritaire"
            checked={selectedColumns.priority}
            name="priority"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Organisme"
            checked={selectedColumns.organism}
            name="organism"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Date d'inscription"
            checked={selectedColumns.createdAt}
            name="createdAt"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Région"
            checked={selectedColumns.region}
            name="region"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Code postal"
            checked={selectedColumns.postalCode}
            name="postalCode"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Souhaités"
            checked={selectedColumns.wantedMentorship}
            name="wantedMentorship"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Affecté"
            checked={selectedColumns.assignedMentorship}
            name="assignedMentorship"
            onChange={handleChange}
          />
          {/* Mentor form questions */}
          <LabelledCheckbox
            label="Recherche d'emploi"
            checked={selectedColumns.jobSearch}
            name="jobSearch"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Carrière"
            checked={selectedColumns.careerPlan}
            name="careerPlan"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Études"
            checked={selectedColumns.studies}
            name="studies"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Entreprenariat"
            checked={selectedColumns.entrepreneurship}
            name="entrepreneurship"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Domaines de compétence"
            checked={selectedColumns.expertiseFields}
            name="expertiseFields"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Formations adaptées"
            checked={selectedColumns.formations}
            name="formations"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Niveau de diplôme"
            checked={selectedColumns.diploma}
            name="diploma"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Mode de vie"
            checked={selectedColumns.consideration}
            name="consideration"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Passe temps"
            checked={selectedColumns.hobby}
            name="hobby"
            onChange={handleChange}
          />
        </CheckboxList>
      </ActionDrawer>

      {/* Barre d'outils au-dessus du tableau */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
        <Button
          variant="outlined"
          onClick={() => setSettingsOpen(true)}
          icon={<SettingsIcon />}
        >
          Colonnes
        </Button>
      </div>
      <List
        columnNumber={columnNumber}
        isEmpty={mentors?.length === 0}
        emptyText="Aucun mentor n'est dispnible pour cette recherche"
        pages={pages}
        page={page}
        setPage={setPage}
        loading={loading}
        activeColumns={activeColumns}
      >
        <OrderTh name="matching" order={order} setOrder={setOrder}>
          Matching
        </OrderTh>
        <Th name="softMatching">Matching soft</Th>
        <Th name="hardMatching">Matching hard</Th>
        <Th name="priority">Prioritaire</Th>
        <OrderTh name="firstName" order={order} setOrder={setOrder}>
          Prénom
        </OrderTh>
        <OrderTh name="lastName" order={order} setOrder={setOrder}>
          Nom
        </OrderTh>
        <OrderTh name="organism" order={order} setOrder={setOrder}>
          Organisme
        </OrderTh>
        <OrderTh name="createdAt" order={order} setOrder={setOrder}>
          Date d'inscription
        </OrderTh>
        <Th name="region">Région</Th>
        <Th name="postalCode">Code postal</Th>
        <Th name="wantedMentorship">Souhaités</Th>
        <Th name="assignedMentorship">Affecté</Th>
        {/* Mentor form questions */}
        <Th name="jobSearch">Recherche d'emploi</Th>
        <Th name="careerPlan">Carrière</Th>
        <Th name="studies">Études</Th>
        <Th name="entrepreneurship">Entreprenariat</Th>
        <Th name="expertiseFields">Compétences</Th>
        <Th name="formations">Formations</Th>
        <Th name="diploma">Diplôme</Th>
        <Th name="consideration"></Th>
        <Th name="hobby">Passe temps</Th>
        <Th name="settings">
          <SettingsContainer onClick={() => setSettingsOpen(true)}>
            <SettingsIcon />
          </SettingsContainer>{' '}
        </Th>
        {mentors?.map((mentor, idx) => (
          <React.Fragment key={idx}>
            <Td name="matching">
              {typeof mentor.matchingValues?.value === 'number' && (
                <MatchingChip value={mentor.matchingValues?.value} />
              )}
            </Td>
            <Td name="softMatching">
              {typeof mentor.matchingValues?.softValue === 'number' && (
                <MatchingChip value={mentor.matchingValues?.softValue} />
              )}
            </Td>
            <Td name="hardMatching">
              {typeof mentor.matchingValues?.hardValue === 'number' && (
                <MatchingChip value={mentor.matchingValues?.hardValue} />
              )}
            </Td>
            <Td name="priority">{mentor.priority ? <Priority /> : null}</Td>
            <BoldTd name="firstName">
              <Link
                to={`/admin/mentoring/mentors/${mentor.id}/mentoring/ongoing/`}
              >
                <EllipsisText text={mentor.firstName || ''} />
              </Link>
            </BoldTd>
            <BoldTd name="lastName">
              <Link
                to={`/admin/mentoring/mentors/${mentor.id}/mentoring/ongoing/`}
              >
                <EllipsisText text={mentor.lastName || ''} />
              </Link>
            </BoldTd>
            <Td name="organism">
              <EllipsisText text={mentor.organism || ''} />
            </Td>
            <Td name="createdAt">
              {mentor.createdAt && getLocaleDate(mentor.createdAt)}
            </Td>
            <Td name="region">
              <EllipsisText text={mentor.region || ''} />
            </Td>
            <Td name="postalCode">{mentor.postalCode}</Td>
            <Td name="wantedMentorship">
              {mentor.MentorForm?.wantedMentorships}
            </Td>
            <Td name="assignedMentorship">{mentor.assignedMentorship}</Td>
            {/* Mentor form questions */}
            <Td name="jobSearch">
              {mentor.MentorForm?.jobSearch &&
                MENTOR_PRIORITY_LEVELS[mentor.MentorForm.jobSearch]}
            </Td>
            <Td name="careerPlan">
              {mentor.MentorForm?.careerPlan &&
                MENTOR_PRIORITY_LEVELS[mentor.MentorForm.careerPlan]}
            </Td>
            <Td name="studies">
              {mentor.MentorForm?.studies &&
                MENTOR_PRIORITY_LEVELS[mentor.MentorForm.studies]}
            </Td>
            <Td name="entrepreneurship">
              {mentor.MentorForm?.entrepreneurship &&
                MENTOR_PRIORITY_LEVELS[mentor.MentorForm.entrepreneurship]}
            </Td>
            <Td name="expertiseFields">
              <EllipsisText
                text={
                  mentor.MentorForm?.expertiseFields
                    ?.map(
                      (expertiseField) => EXPERTISE[expertiseField.expertise],
                    )
                    .join('; ') || ''
                }
              />
            </Td>
            <Td name="formations">
              <EllipsisText
                text={
                  mentor.MentorForm?.formations
                    ?.map(
                      (mentorFormation) =>
                        FORMATIONS[mentorFormation.formation],
                    )
                    .join('; ') || ''
                }
              />
            </Td>
            <Td name="diploma">
              <EllipsisText
                text={
                  mentor.MentorForm?.diploma
                    ?.map(
                      (mentorDiploma) => STUDY_LEVELS[mentorDiploma.diploma],
                    )
                    .join('; ') || ''
                }
              />
            </Td>
            <Td name="consideration">
              {mentor.MentorForm?.consideration &&
                CONSIDERATIONS[mentor.MentorForm.consideration]}
            </Td>
            <Td name="hobby">
              {mentor.MentorForm?.hobby && HOBBIES[mentor.MentorForm.hobby]}
            </Td>
            <Td name="settings">
              <ChooseMentorContainer>
                {mentor.isCurrentMentor ? (
                  <CurrentMentor>Mentor actuel</CurrentMentor>
                ) : (
                  <ChooseMentor
                    menteeId={mentee?.id || -1}
                    mentorId={mentor.id || -1}
                    redirect={`/admin/mentoring/mentees/${mentee?.id}/mentoring/`}
                  >
                    Selectionner comme mentor
                  </ChooseMentor>
                )}
              </ChooseMentorContainer>
            </Td>
          </React.Fragment>
        ))}
      </List>
    </>
  );
};

const mapStateToProps = (state: StoreState) => {
  return {
    activeColumns: state.admin.menteeLists.associate.activeColumns,
  };
};

const mapDispatchToProps = (dispatch: (action: Action) => void) => {
  return {
    setActiveColumns: (activeColumns: Columns) =>
      dispatch(setActiveColumns<Columns>('associate', activeColumns)),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(MentoringList);
